// Affiliates — the company's partner program dashboard.
//
// Add partners (they get an emailed portal login + tracked link), set each
// partner's commission %, record referred sales, and mark commissions paid.
import { useEffect, useState } from "react";
import { Copy, Handshake, Plus, DollarSign, CheckCircle2 } from "lucide-react";
import { Shell } from "../components/Shell";
import { Modal } from "../components/Modal";
import { request } from "../lib/api";

interface Affiliate {
  id: string; name: string; email: string; code: string; link: string;
  commission_pct: number; destination_url: string | null; status: string;
  clicks: number; referral_count: number;
  commission_owed: number; commission_paid: number; sales_total: number;
}
interface Referral {
  id: string; description: string; sale_amount: number; commission_amount: number;
  status: string; created_at: string; client_name: string | null;
}

const money = (n: number) => `$${Number(n || 0).toLocaleString(undefined, { maximumFractionDigits: 2 })}`;

export default function AffiliatesPage() {
  const [list, setList] = useState<Affiliate[]>([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState<string | null>(null);
  const [addOpen, setAddOpen] = useState(false);
  const [detail, setDetail] = useState<Affiliate | null>(null);
  const [ledger, setLedger] = useState<Referral[]>([]);
  const [msg, setMsg] = useState<string | null>(null);

  // add form
  const [fName, setFName] = useState(""); const [fEmail, setFEmail] = useState("");
  const [fPct, setFPct] = useState("40"); const [fDest, setFDest] = useState("");
  const [fBiz, setFBiz] = useState(""); const [fPhone, setFPhone] = useState("");
  const [busy, setBusy] = useState(false);

  // record sale form
  const [saleDesc, setSaleDesc] = useState(""); const [saleAmt, setSaleAmt] = useState("");
  // edit pct
  const [editPct, setEditPct] = useState("");

  async function load() {
    try {
      const r = await request<{ ok: true; affiliates: Affiliate[] }>("/api/affiliates");
      setList(r.affiliates);
    } catch (e: any) { setErr(e?.message || "Could not load affiliates."); }
    finally { setLoading(false); }
  }
  useEffect(() => { load(); }, []);

  async function openDetail(a: Affiliate) {
    setDetail(a); setEditPct(String(a.commission_pct)); setLedger([]);
    try {
      const r = await request<{ ok: true; referrals: Referral[] }>(`/api/affiliates/${a.id}`);
      setLedger(r.referrals);
    } catch { /* ledger stays empty */ }
  }

  async function addPartner() {
    setBusy(true); setMsg(null);
    try {
      const r = await request<any>("/api/affiliates", {
        method: "POST",
        body: { name: fName.trim() || undefined, email: fEmail.trim(), commission_pct: Number(fPct), destination_url: fDest.trim() || undefined,
                business_name: fBiz.trim() || undefined, phone: fPhone.trim() || undefined },
      });
      setMsg(r.email_delivered
        ? `${r.affiliate.name} added — their login was emailed. They'll set their name and business on first sign-in.`
        : `${r.affiliate.name} added, but the email FAILED. Temp password: ${r.temp_password} — send it to them yourself.`);
      setAddOpen(false); setFName(""); setFEmail(""); setFPct("40"); setFDest(""); setFBiz(""); setFPhone("");
      load();
    } catch (e: any) { setMsg(e?.message || "Could not add the partner."); }
    finally { setBusy(false); }
  }

  async function recordSale() {
    if (!detail) return;
    setBusy(true);
    try {
      await request<any>(`/api/affiliates/${detail.id}`, {
        method: "POST",
        body: { action: "record_sale", description: saleDesc.trim(), sale_amount: Number(saleAmt.replace(/[$,\s]/g, "")) },
      });
      setSaleDesc(""); setSaleAmt("");
      openDetail(detail); load();
    } catch (e: any) { setMsg(e?.message || "Could not record the sale."); }
    finally { setBusy(false); }
  }

  async function markPaid() {
    if (!detail) return;
    // Money moves and the partner sees a payout statement — ask before doing it.
    if (!window.confirm(
      `Mark all outstanding commissions PAID for ${detail.name}?\n\nThis writes a payout statement the partner will see in their portal. It cannot be undone from here.`
    )) return;
    setBusy(true);
    try {
      const r = await request<any>(`/api/affiliates/${detail.id}`, { method: "POST", body: { action: "mark_paid", confirm: true } });
      setMsg(`Marked ${r.marked_paid} commission(s) paid for ${detail.name}.`);
      openDetail(detail); load();
    } catch (e: any) { setMsg(e?.message || "Could not mark paid."); }
    finally { setBusy(false); }
  }

  async function savePct() {
    if (!detail) return;
    setBusy(true);
    try {
      await request<any>(`/api/affiliates/${detail.id}`, { method: "PATCH", body: { commission_pct: Number(editPct) } });
      setMsg(`Commission set to ${editPct}% for ${detail.name} (applies to future sales).`);
      load();
    } catch (e: any) { setMsg(e?.message || "Could not update."); }
    finally { setBusy(false); }
  }

  return (
    <Shell title="Affiliates" subtitle="Your partner program — links, commissions, payouts" actions={
      <button className="primary-button" onClick={() => setAddOpen(true)}>
        <Plus size={14} aria-hidden="true" /> Add partner
      </button>
    }>
      {msg && <p className="muted small" style={{ marginBottom: 12 }}>{msg}</p>}
      {loading && <p className="muted small">Loading…</p>}
      {err && <p className="error-text">{err}</p>}

      {!loading && list.length === 0 && !err && (
        <div className="panel" style={{ textAlign: "center", padding: 40 }}>
          <Handshake size={34} aria-hidden="true" style={{ color: "var(--ne-chrome-2)", marginBottom: 10 }} />
          <h2 style={{ marginBottom: 6 }}>No partners yet</h2>
          <p className="muted small" style={{ maxWidth: 480, margin: "0 auto 16px" }}>
            Add an affiliate partner and they get their own tracked link and a portal login to watch
            their clicks, referrals, and commissions. You set each partner's percentage.
          </p>
          <button className="primary-button" onClick={() => setAddOpen(true)}>Add your first partner</button>
        </div>
      )}

      {list.length > 0 && (
        <div className="panel">
          <div className="table-list">
            {list.map((a) => (
              <div key={a.id} className="table-row table-row--wide" style={{ alignItems: "center", cursor: "pointer" }} onClick={() => openDetail(a)}>
                <div style={{ flex: "1 1 220px", minWidth: 0 }}>
                  <strong>{(a as any).business_name || a.name}</strong> <span className={`pill ${a.status === "active" ? "pill-green" : ""}`}>{a.status}</span>
                  <div className="muted small">{(a as any).business_name ? `${a.name} · ` : ""}{a.email}</div>
                </div>
                <span style={{ minWidth: 60, fontWeight: 700 }}>{a.commission_pct}%</span>
                <span className="muted small" style={{ minWidth: 70 }}>{a.clicks} clicks</span>
                <span className="muted small" style={{ minWidth: 70 }}>{(a as any).client_count ?? 0} clients</span>
                <span className="muted small" style={{ minWidth: 70 }}>{a.referral_count} sales</span>
                <span style={{ minWidth: 100, color: "var(--ne-warning-text)" }}>{money(a.commission_owed)} owed</span>
                <span className="muted small" style={{ minWidth: 90 }}>{money(a.commission_paid)} paid</span>
                <button className="ghost-button" onClick={(e) => { e.stopPropagation(); navigator.clipboard.writeText(a.link); setMsg(`Link copied: ${a.link}`); }}>
                  <Copy size={13} aria-hidden="true" /> Link
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* add partner */}
      <Modal open={addOpen} title="Add affiliate partner" onClose={() => setAddOpen(false)}
        footer={<>
          <button className="ghost-button" onClick={() => setAddOpen(false)}>Cancel</button>
          <button className="primary-button" disabled={busy || !fEmail.includes("@")} onClick={addPartner}>
            {busy ? "Adding…" : "Add & send login"}
          </button>
        </>}>
        <div className="stacked-form">
          <label className="form-field"><span>Email (their portal login) — the only thing you need</span>
            <input value={fEmail} onChange={(e) => setFEmail(e.target.value)} inputMode="email" placeholder="jordan@example.com" /></label>
          <details style={{ marginTop: 4 }}>
            <summary className="muted small" style={{ cursor: "pointer" }}>Already know their name and business? (optional)</summary>
            <div className="stacked-form" style={{ marginTop: 10 }}>
              <label className="form-field"><span>Name</span>
                <input value={fName} onChange={(e) => setFName(e.target.value)} placeholder="Jordan Smith" /></label>
              <div className="form-row-2">
                <label className="form-field"><span>Business name (goes in their link)</span>
                  <input value={fBiz} onChange={(e) => setFBiz(e.target.value)} placeholder="Smith Tax & Credit" /></label>
                <label className="form-field"><span>Phone</span>
                  <input value={fPhone} onChange={(e) => setFPhone(e.target.value)} inputMode="tel" /></label>
              </div>
              {fBiz.trim() && (
                <p className="muted small">Their branded link will be <code>alieffundinghub.com/with/{fBiz.trim().toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-+|-+$/g, "")}</code></p>
              )}
            </div>
          </details>
          <div className="form-row-2">
            <label className="form-field"><span>Their share of your success fee (%)</span>
              <input value={fPct} onChange={(e) => setFPct(e.target.value)} inputMode="decimal" />
              <span className="muted small">40 = they get 40% of the fee you collect (e.g. 40% of a 10% fee).</span></label>
            <label className="form-field"><span>Link destination (optional)</span>
              <input value={fDest} onChange={(e) => setFDest(e.target.value)} placeholder="https:// where their link sends people" /></label>
          </div>
          <p className="muted small">
            They get an email with a portal login. On first sign-in they enter their own name and
            business name, which creates their branded referral link — then they see every client
            they refer, approvals, commissions, and payouts.
          </p>
        </div>
      </Modal>

      {/* partner detail */}
      <Modal open={!!detail} title={detail ? `${detail.name} — ${detail.commission_pct}%` : ""} onClose={() => setDetail(null)} wide
        footer={<button className="ghost-button" onClick={() => setDetail(null)}>Close</button>}>
        {detail && (
          <>
            <div style={{ display: "flex", gap: 10, flexWrap: "wrap", marginBottom: 14 }}>
              <button className="ghost-button" onClick={() => { navigator.clipboard.writeText(detail.link); setMsg("Link copied."); }}>
                <Copy size={13} aria-hidden="true" /> Copy their link
              </button>
              <label className="form-field" style={{ flexDirection: "row", alignItems: "center", gap: 8 }}>
                <input style={{ width: 70 }} value={editPct} onChange={(e) => setEditPct(e.target.value)} inputMode="decimal" />
                <button className="ghost-button" disabled={busy} onClick={savePct}>Set %</button>
              </label>
              <button className="primary-button" disabled={busy || detail.commission_owed <= 0} onClick={markPaid}>
                <CheckCircle2 size={14} aria-hidden="true" /> Mark {money(detail.commission_owed)} paid
              </button>
            </div>

            <div className="sub-panel" style={{ marginBottom: 14 }}>
              <strong style={{ display: "block", marginBottom: 8 }}><DollarSign size={14} aria-hidden="true" /> Record a referred sale</strong>
              <div className="form-row-2">
                <label className="form-field"><span>What was sold</span>
                  <input value={saleDesc} onChange={(e) => setSaleDesc(e.target.value)} placeholder="Funding deal — Jane D." /></label>
                <label className="form-field"><span>Success fee you collected ($)</span>
                  <input value={saleAmt} onChange={(e) => setSaleAmt(e.target.value)} inputMode="decimal" placeholder="2500" />
                  <span className="muted small">Your fee, not the loan amount — their {detail.commission_pct}% comes off this.</span></label>
              </div>
              <button className="primary-button" style={{ marginTop: 8 }} disabled={busy || !saleDesc.trim() || !saleAmt.trim()} onClick={recordSale}>
                Record — partner gets {detail.commission_pct}% of this fee
              </button>
            </div>

            <strong style={{ display: "block", marginBottom: 8 }}>Commission ledger</strong>
            {ledger.length === 0 && <p className="muted small">No referred sales recorded yet.</p>}
            {ledger.length > 0 && (
              <div className="table-list">
                {ledger.map((r) => (
                  <div key={r.id} className="table-row table-row--wide">
                    <span style={{ flex: "1 1 200px" }}>{r.description}</span>
                    <span className="muted small" style={{ minWidth: 90 }}>{money(r.sale_amount)} sale</span>
                    <strong style={{ minWidth: 90 }}>{money(r.commission_amount)}</strong>
                    <span className={`pill ${r.status === "paid" ? "pill-green" : r.status === "void" ? "pill-red" : ""}`}>{r.status}</span>
                    <span className="muted small">{new Date(r.created_at).toLocaleDateString()}</span>
                  </div>
                ))}
              </div>
            )}
          </>
        )}
      </Modal>
    </Shell>
  );
}
